import { ReactNode } from 'react';
import { history } from 'instantsearch.js/es/lib/routers';

import { Configure, InstantSearch } from 'react-instantsearch';
import algoliasearch from 'algoliasearch/lite';
import { useLocation, useSearchParams } from 'react-router-dom';

// Default UI State
import type { UiState } from 'instantsearch.js';

const searchClient = algoliasearch("BCXURW325T", "f9ee2c1748b8d3914488faf0da618532");

// Desired Route State
type RouteState = {
    query?: string;
    page?: string;
    brands?: string[];
    materialType?: string[];
    category?: string;
    subcategory?: string[];
    color?: string[]
    design?: string[]
    price?: string[]
    offering?: string[]
    supplier?: string[]
    application?: string[]
    globalAvailability?: string[]
    bleachCleanable?: string[]
    choicePerformance?: string[]
    chemicalContent?: string[]
    flammability?: string[]
    certificationsAndStandards?: string[]
    productLineAvailability?: string[]
    isHighAbrasion?: string[]
    laminateType?: string[]
    materialContent?: string[]
    species?: string[]
    sub_type?: string[]
    veneerType?: string[]
    sortBy?: string;
    hitsPerPage?: string;
    initialClose?: boolean;
    productSearch?: boolean;
};

const routeStateDefaultValues: RouteState = {
    query: '',
    page: '1',
    brands: undefined,
    category: '',
    sortBy: 'instant_search',
    materialType: undefined
};


interface InstantSearchWrapperProps {
    indexName: string;
    categoryPage?: string;
    productsToFilter?: string[];
    children: ReactNode;
}

function getCategoryName(slug: string) {
    return slug
        .split('+')
        .map(decodeURIComponent)
        .join(' ');
}

const getStateMapping = ({ indexName }: { indexName: string }) => ({
    stateToRoute(uiState: UiState): RouteState {
        const indexUiState = uiState[indexName];
        return {
            query: indexUiState.query,
            page: (indexUiState.page && String(indexUiState.page)) || undefined,
            materialType: indexUiState.refinementList && indexUiState.refinementList['filters.materialType'],
            color: indexUiState.refinementList && indexUiState.refinementList['filters.color'],
            design: indexUiState.refinementList && indexUiState.refinementList['filters.design'],
            price: indexUiState.refinementList && indexUiState.refinementList['filters.price'],
            brands: indexUiState.refinementList && indexUiState.refinementList['filters.brand'],
            offering: indexUiState.refinementList && indexUiState.refinementList['filters.offering'],
            supplier: indexUiState.refinementList && indexUiState.refinementList['filters.supplier'],
            application: indexUiState.refinementList && indexUiState.refinementList['filters.application'],
            globalAvailability: indexUiState.refinementList && indexUiState.refinementList['filters.globalAvailability'],
            bleachCleanable: indexUiState.refinementList && indexUiState.refinementList.bleachCleanable,
            choicePerformance: indexUiState.refinementList && indexUiState.refinementList.choicePerformance,
            chemicalContent: indexUiState.refinementList && indexUiState.refinementList.chemicalContent,
            certificationsAndStandards: indexUiState.refinementList && indexUiState.refinementList.certificationAndStandards,
            productLineAvailability: indexUiState.refinementList && indexUiState.refinementList['filters.productLineAvailability'],
            subcategory: indexUiState.refinementList && indexUiState.refinementList['filters.subcategory'],
            flammability: indexUiState.refinementList && indexUiState.refinementList['filters.flammability'],
            isHighAbrasion: indexUiState.refinementList && indexUiState.refinementList['filters.isHighAbrasion'],
            laminateType: indexUiState.refinementList && indexUiState.refinementList['filters.laminateType'],
            materialContent: indexUiState.refinementList && indexUiState.refinementList['filters.materialContent'],
            species: indexUiState.refinementList && indexUiState.refinementList['filters.species'], 
            veneerType: indexUiState.refinementList && indexUiState.refinementList['filters.veneerType'],
            sub_type: indexUiState.refinementList && indexUiState.refinementList['filters.sub_type'],
            sortBy: indexUiState.sortBy,
            initialClose: undefined,
            productSearch: undefined
        };
    },
    routeToState(routeState: RouteState): UiState {
    
        const refinementList: { [key: string]: string[] } = {};

        if(routeState.application) refinementList['filters.application'] = routeState.application;

        if(routeState.choicePerformance) refinementList['filters.choicePerformance'] = routeState.choicePerformance;

        if(routeState.bleachCleanable) refinementList['filters.bleachCleanable'] = routeState.bleachCleanable;

        if(routeState.certificationsAndStandards) refinementList['filters.certificationsAndStandards'] = routeState.certificationsAndStandards;

        if(routeState.chemicalContent) refinementList['filters.chemicalContent'] = routeState.chemicalContent;

        if(routeState.brands) refinementList['filters.brands'] = routeState.brands;
        
        if(routeState.materialType) refinementList['filters.materialType'] = routeState.materialType;

        if(routeState.color) refinementList['filters.color'] = routeState.color;

        if(routeState.design) refinementList['filters.design'] = routeState.design;
        
        if(routeState.price) refinementList['filters.price'] = routeState.price;

        if(routeState.globalAvailability) refinementList['filters.globalAvailability'] = routeState.globalAvailability;
        
        if(routeState.offering) refinementList['filters.offering'] = routeState.offering;
        
        if(routeState.supplier) refinementList['filters.supplier'] = routeState.supplier;
        
        if(routeState.productLineAvailability) refinementList['filters.productLineAvailability'] = routeState.productLineAvailability;
        
        if(routeState.subcategory) refinementList['filters.subcategory'] = routeState.subcategory;
        
        if(routeState.flammability) refinementList['filters.flammability'] = routeState.flammability;

        if(routeState.isHighAbrasion) refinementList['filters.isHighAbrasion'] = routeState.isHighAbrasion;

        if(routeState.laminateType) refinementList['filters.laminateType'] = routeState.laminateType;

        if(routeState.materialContent) refinementList['filters.materialContent'] = routeState.materialContent;

        if(routeState.species) refinementList['filters.species'] = routeState.species;

        if(routeState.sub_type) refinementList['filters.sub_type'] = routeState.sub_type;

        if(routeState.veneerType) refinementList['filters.veneerType'] = routeState.veneerType;
    
        return {
            [indexName]: {
                query: routeState.query,
                page: Number(routeState.page),
                refinementList,
                sortBy: routeState.sortBy,
            },
        };
      },

});

export default function InstantSearchWrapper({ indexName, children, categoryPage = "", productsToFilter = [] }: InstantSearchWrapperProps) {

    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const routing = {
        router: history<RouteState>({
            windowTitle({ category, query }) {
                const queryTitle = query ? `Results for "${query}"` : 'Search';
          
                if (category) {
                  return `${category} – ${queryTitle}`;
                }
          
                return queryTitle;
            },
            createURL({ qsModule, routeState, location }) {

                const { hash, pathname } = location;

                const { initialClose, productSearch } = qsModule.parse(location.search.slice(1))

                const queryParams: any = {};
                const pathnameMatches = pathname.match(/^\/?(?:[a-z]{2}-[a-z]{2}\/)?(.*?)\/?$/);
                const restOfPath = pathnameMatches?.[1] || '';
                
                if(routeState.query) queryParams.query = encodeURIComponent(routeState.query);

                if(routeState.page !== "1") queryParams.page = routeState.page;

                if(routeState.subcategory) queryParams.subcategory = routeState.subcategory.map(encodeURIComponent);

                if(routeState.brands) queryParams.brands = routeState.brands.map(encodeURIComponent);
                
                if(routeState.materialType) queryParams.materialType = routeState.materialType.map(encodeURIComponent);

                if(routeState.color) queryParams.color = routeState.color.map(encodeURIComponent);

                if(routeState.price) queryParams.price = routeState.price.map(encodeURIComponent);
                
                if(routeState.design) queryParams.design = routeState.design.map(encodeURIComponent);

                if(routeState.globalAvailability) queryParams.globalAvailability = routeState.globalAvailability.map(encodeURIComponent);

                if(routeState.offering) queryParams.offering = routeState.offering.map(encodeURIComponent);

                if(routeState.supplier) queryParams.supplier = routeState.supplier.map(encodeURIComponent);

                if(routeState.productLineAvailability) queryParams.productLineAvailability = routeState.productLineAvailability.map(encodeURIComponent);
                
                if(routeState.application) queryParams.application = routeState.application.map(encodeURIComponent);

                if(routeState.bleachCleanable) queryParams.bleachCleanable = routeState.bleachCleanable.map(encodeURIComponent);

                if(routeState.chemicalContent) queryParams.chemicalContent = routeState.chemicalContent.map(encodeURIComponent);

                if(routeState.choicePerformance) queryParams.choicePerformance = routeState.choicePerformance.map(encodeURIComponent);

                if(routeState.flammability) queryParams.flammability = routeState.flammability.map(encodeURIComponent);

                if(routeState.isHighAbrasion) queryParams.isHighAbrasion = routeState.isHighAbrasion.map(encodeURIComponent);

                if(routeState.laminateType) queryParams.laminateType = routeState.laminateType.map(encodeURIComponent);

                if(routeState.species) queryParams.species = routeState.species.map(encodeURIComponent);

                if(routeState.sub_type) queryParams.sub_type = routeState.sub_type.map(encodeURIComponent);

                if(routeState.veneerType) queryParams.veneerType = routeState.veneerType.map(encodeURIComponent);

                if (routeState.materialContent) queryParams.materialContent = routeState.materialContent.map(encodeURIComponent);
                
                if(initialClose) queryParams.initialClose = 'true';

                if(productSearch) queryParams.productSearch = 'true';
                
                const queryString = qsModule.stringify(queryParams, {
                    addQueryPrefix: true,
                    arrayFormat: 'repeat',
                });
                return `/${restOfPath}${queryString}${hash}`;
            },
            parseURL({ qsModule, location }): RouteState {
                const pathnameMatches = location.pathname.match(/\/(?:[a-z]{2}-[a-z]{2}\/)?([a-z]+)\/?(\?.*)?$/);
                
                
                const category = getCategoryName((pathnameMatches?.[1] || ''));

                const { 
                    query = '', 
                    page, 
                    price = [], 
                    subcategory = [], 
                    materialType = [], 
                    brands = [], 
                    color = [], 
                    design = [], 
                    globalAvailability = [], 
                    offering = [], 
                    supplier = [], 
                    productLineAvailability = [], 
                    application = [], 
                    bleachCleanable = [],
                    chemicalContent = [],
                    choicePerformance = [],
                    flammability = [],
                    isHighAbrasion = [],
                    laminateType = [],
                    species = [],
                    sub_type = [],
                    veneerType = [],
                    materialContent = [],
                    initialClose, 
                    productSearch 
                } = qsModule.parse(location.search.slice(1));

                const allSubcategories = (Array.isArray(subcategory)
                    ? subcategory
                    : [subcategory].filter(Boolean)) as string[]

                const allColors = (Array.isArray(color)
                    ? color
                    : [color].filter(Boolean)) as string[]

                const allBrands = (Array.isArray(brands) 
                    ? brands
                    : [brands].filter(Boolean)) as string[];

                const allMaterials = (Array.isArray(materialType) 
                    ? materialType
                    : [materialType].filter(Boolean)) as string[];

                const allDesigns = (Array.isArray(design) 
                    ? design
                    : [design].filter(Boolean)) as string[];

                const allGlobalAvailabilities = (Array.isArray(globalAvailability)
                    ? globalAvailability
                    : [globalAvailability].filter(Boolean)) as string[];

                const allOfferings = (Array.isArray(offering)
                    ? offering
                    : [offering].filter(Boolean)) as string[];
                
                const allSuppliers = (Array.isArray(supplier)
                    ? supplier
                    : [supplier].filter(Boolean)) as string[];
                
                const allProducts = (Array.isArray(productLineAvailability)
                    ? productLineAvailability
                    : [productLineAvailability].filter(Boolean)) as string[];

                const allPriceGroups = (Array.isArray(price)
                    ? price
                    : [price].filter(Boolean)) as string[];

                const allApplications = (Array.isArray(application)
                    ? application
                    : [application].filter(Boolean)) as string[];

                const allBleachCleanableValues = (Array.isArray(bleachCleanable)
                    ? bleachCleanable
                    : [bleachCleanable].filter(Boolean)) as string[];

                const allChemicalContentValues = (Array.isArray(chemicalContent)
                    ? chemicalContent
                    : [chemicalContent].filter(Boolean)) as string[];
                    
                const allChoicePerformance = (Array.isArray(choicePerformance)
                    ? choicePerformance
                    : [choicePerformance].filter(Boolean)) as string[];

                const allFlammability = (Array.isArray(flammability)
                    ? flammability
                    : [flammability].filter(Boolean)) as string[];

                const allIsHighAbrasion = (Array.isArray(isHighAbrasion)
                    ? isHighAbrasion
                    : [isHighAbrasion].filter(Boolean)) as string[];

                const allLaminateTypes = (Array.isArray(laminateType)
                    ? laminateType
                    : [laminateType].filter(Boolean)) as string[];
                
                const allSpecies = (Array.isArray(species)
                    ? species
                    : [species].filter(Boolean)) as string[];

                const allSubtypes = (Array.isArray(sub_type)
                    ? sub_type
                    : [sub_type].filter(Boolean)) as string[];

                const allVeneerTypes = (Array.isArray(veneerType)
                    ? veneerType
                    : [veneerType].filter(Boolean)) as string[];

                const allMaterialContent = (Array.isArray(materialContent)
                    ? materialContent
                    : [materialContent].filter(Boolean)) as string[];

                return {
                    query: decodeURIComponent(query as string),
                    page: page as string,
                    subcategory: allSubcategories.map(decodeURIComponent),
                    price: allPriceGroups.map(decodeURIComponent),
                    color: allColors.map(decodeURIComponent),
                    design: allDesigns.map(decodeURIComponent),
                    materialType: allMaterials.map(decodeURIComponent),
                    brands: allBrands.map(decodeURIComponent),
                    globalAvailability: allGlobalAvailabilities.map(decodeURIComponent),
                    offering: allOfferings.map(decodeURIComponent),
                    supplier: allSuppliers.map(decodeURIComponent),
                    productLineAvailability: allProducts.map(decodeURIComponent),
                    application: allApplications.map(decodeURIComponent),
                    bleachCleanable: allBleachCleanableValues.map(decodeURIComponent),
                    chemicalContent: allChemicalContentValues.map(decodeURIComponent),
                    choicePerformance: allChoicePerformance.map(decodeURIComponent),
                    flammability: allFlammability.map(decodeURIComponent),
                    isHighAbrasion: allIsHighAbrasion.map(decodeURIComponent),
                    laminateType: allLaminateTypes.map(decodeURIComponent),
                    species: allSpecies.map(decodeURIComponent),
                    sub_type: allSubtypes.map(decodeURIComponent), 
                    veneerType: allVeneerTypes.map(decodeURIComponent),
                    materialContent: allMaterialContent.map(decodeURIComponent),
                    category,
                    initialClose: initialClose === 'true',
                    productSearch: productSearch === 'true'
                }
            },
            push(url) {
                const query = url.split("?")
                const params = new URLSearchParams(query[1]);
                const decomposedParams = new URLSearchParams(window.location.search);

                params.forEach((key, value) => {
                    decomposedParams.delete(value)
                })


                params.forEach((value, key) => {
                    decomposedParams.append(key, value) 
                })

                const isProductPage = ["products", "produkte", "produits", "productos"].some(productUrl => location.pathname.includes(productUrl))

                if(!isProductPage) {
                    setSearchParams(decomposedParams, { replace: true })
                }  
                
            }
        }),
        stateMapping: getStateMapping({indexName})
    };

    return (
        <InstantSearch searchClient={searchClient} indexName={indexName} routing={routing} key={categoryPage}>
            <Configure 
                filters={categoryPage 
                            ? `category.code:${categoryPage}` 
                            : productsToFilter && productsToFilter.length > 0
                                ? productsToFilter.map(product => `filters.productLineAvailability:"${product}"`).join(' OR ') 
                                : ""
                        } 
            />
            { children }
        </InstantSearch>
    )
}